(() => {
  'use strict';

  function initializeCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const routes = window.SouqizaRoutes;
    if (!drawer || drawer.dataset.ready || typeof drawer.showModal !== 'function' || !window.fetch || !window.FormData || !routes) return;

    let endpoints;
    try {
      endpoints = Object.fromEntries(['cart', 'add', 'change'].map(key => {
        if (typeof routes[key] !== 'string' || !routes[key]) throw new Error('Missing cart route');
        const url = new URL(routes[key], location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid cart route');
        url.pathname = url.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
        return [key, url.href];
      }));
    } catch (error) {
      return; // Leave links and native product forms untouched if configuration is incomplete.
    }

    const items = drawer.querySelector('[data-cart-items]');
    const status = drawer.querySelector('[data-cart-status]');
    const refresh = drawer.querySelector('[data-cart-refresh]');
    const content = drawer.querySelector('[data-cart-content]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const checkout = drawer.querySelector('[data-cart-checkout]');
    const close = drawer.querySelector('[data-cart-close]');
    const gift = drawer.querySelector('[data-cart-gift]');
    const addon = drawer.querySelector('[data-cart-addon]');
    let cart = null;
    let fresh = false;
    let uncertainMutation = false;
    let pending = 0;
    let queue = Promise.resolve();
    let trigger = null;
    const locked = new Map();

    function standardForm(form, submitter) {
      if (!(form instanceof HTMLFormElement) || !form.matches('.commerce-form') || form.method.toLowerCase() !== 'post') return false;
      if (form.matches('[data-cart-native], [data-requires-selling-plan="true"]') || (form.target && form.target !== '_self')) return false;
      if (submitter && (submitter.hasAttribute('formaction') || submitter.hasAttribute('formmethod') || submitter.hasAttribute('formtarget') || !['', 'add'].includes(submitter.name))) return false;
      const action = new URL(form.action, location.href);
      const add = new URL(endpoints.add);
      if (action.origin !== add.origin || action.pathname.replace(/\/$/, '').replace(/\.js$/, '') !== add.pathname.replace(/\.js$/, '') || action.search) return false;
      // Subscription, bundle and redirect flows remain under their native/app owner.
      return !Array.from(form.elements).some(control => control.name === 'selling_plan' || control.name === 'return_to' || control.name === 'checkout' || /^items\[/.test(control.name));
    }

    function syncControls() {
      content.setAttribute('aria-busy', String(pending > 0));
      drawer.dataset.busy = String(pending > 0);
      items.querySelectorAll('button, input').forEach(control => { control.disabled = pending > 0 || !fresh || control.dataset.unavailable === 'true'; });
      checkout.disabled = pending > 0 || !fresh || !cart?.item_count;
      refresh.disabled = pending > 0;
      if (pending) {
        Array.from(document.forms).filter(form => standardForm(form)).forEach(form => {
          Array.from(form.elements).forEach(control => {
            if (!('disabled' in control) || locked.has(control)) return;
            locked.set(control, control.disabled);
            control.disabled = true;
          });
        });
      } else {
        locked.forEach((disabled, control) => { control.disabled = disabled; });
        locked.clear();
      }
    }

    function announce(message, error = false) {
      status.textContent = message;
      status.classList.toggle('is-error', error);
    }

    function element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }

    function safeUrl(value) {
      if (!value) return null;
      try {
        const url = new URL(value, location.origin);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
      } catch (error) {
        return null;
      }
    }

    function render(next) {
      if (!Array.isArray(next.items) || typeof next.currency !== 'string' || !Number.isFinite(next.total_price)) throw new Error('The cart response could not be read.');
      const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: next.currency });
      // Shopify appends hundredths even for JPY/KRW. Intl controls display decimals, not the divisor.
      const money = amount => formatter.format(Number(amount) / 100);
      const fragment = document.createDocumentFragment();
      next.items.forEach((item, index) => {
        const row = element('li', 'cart-drawer__item');
        row.dataset.key = item.key;
        const imageUrl = safeUrl(item.featured_image?.url || item.image);
        if (imageUrl) {
          const image = element('img', 'cart-drawer__image');
          image.src = imageUrl;
          image.alt = item.featured_image?.alt || '';
          image.width = 100;
          image.height = 100;
          image.loading = 'lazy';
          row.append(image);
        } else {
          const placeholder = element('span', 'cart-drawer__image cart-drawer__image--empty', 'SQ.');
          placeholder.setAttribute('aria-hidden', 'true');
          row.append(placeholder);
        }
        const details = element('div', 'cart-drawer__details');
        const heading = element('h3');
        const productUrl = safeUrl(item.url);
        const title = element(productUrl ? 'a' : 'span', '', item.product_title || item.title);
        if (productUrl) title.href = productUrl;
        heading.append(title);
        details.append(heading);
        if (item.variant_title && item.variant_title !== 'Default Title') details.append(element('p', 'cart-drawer__variant', item.variant_title));
        if (item.selling_plan_allocation?.selling_plan?.name) details.append(element('p', 'cart-drawer__variant', item.selling_plan_allocation.selling_plan.name));
        Object.entries(item.properties || {}).forEach(([name, value]) => {
          if (!name.startsWith('_') && value != null && String(value).trim()) details.append(element('p', 'cart-drawer__property', `${name}: ${value}`));
        });
        const prices = element('p', 'cart-drawer__line-price');
        if (item.original_line_price > item.final_line_price) {
          const original = element('s', '', money(item.original_line_price));
          original.setAttribute('aria-label', `Original line price ${money(item.original_line_price)}`);
          prices.append(original);
        }
        prices.append(element('strong', '', money(item.final_line_price)));
        details.append(prices);
        (item.line_level_discount_allocations || []).forEach(discount => {
          details.append(element('p', 'cart-drawer__discount', `${discount.discount_application.title}: -${money(discount.amount)}`));
        });
        if (item.unit_price_measurement && item.unit_price != null) {
          const unit = item.unit_price_measurement;
          details.append(element('p', 'cart-drawer__variant', `${money(item.unit_price)} / ${unit.reference_value === 1 ? '' : unit.reference_value}${unit.reference_unit}`));
        }
        const controls = element('div', 'cart-drawer__controls');
        const stepper = element('div', 'cart-drawer__stepper');
        const rule = item.quantity_rule || {};
        const min = Number(rule.min) || 1;
        const step = Number(rule.increment) || 1;
        const quantityLocked = item.instructions?.can_update_quantity === false;
        const removeLocked = item.instructions?.can_remove === false;
        ['minus', 'quantity', 'plus'].forEach(action => {
          const control = element(action === 'quantity' ? 'input' : 'button', '', action === 'minus' ? '\u2212' : action === 'plus' ? '+' : null);
          control.dataset.cartAction = action;
          control.setAttribute('aria-label', `${action === 'quantity' ? 'Quantity for' : action === 'minus' ? 'Decrease quantity for' : 'Increase quantity for'} ${item.product_title || item.title}`);
          if (action === 'quantity') {
            control.type = 'number';
            control.inputMode = 'numeric';
            control.min = String(min);
            control.step = String(step);
            if (rule.max != null) control.max = String(rule.max);
            control.value = String(item.quantity);
            control.required = true;
          } else {
            control.type = 'button';
          }
          control.dataset.unavailable = String(quantityLocked || (action === 'minus' && item.quantity <= min) || (action === 'plus' && rule.max != null && item.quantity + step > rule.max));
          stepper.append(control);
        });
        const remove = element('button', 'cart-drawer__text-button', 'Remove');
        remove.type = 'button';
        remove.dataset.cartAction = 'remove';
        remove.dataset.unavailable = String(removeLocked);
        remove.setAttribute('aria-label', `Remove ${item.product_title || item.title}`);
        controls.append(stepper, remove);
        details.append(controls);
        row.dataset.index = String(index);
        row.append(details);
        fragment.append(row);
      });
      items.replaceChildren(fragment);
      drawer.querySelector('[data-cart-empty]').hidden = next.item_count !== 0;
      drawer.querySelector('[data-cart-drawer-count]').textContent = `(${next.item_count})`;
      drawer.querySelector('[data-cart-subtotal]').textContent = money(next.total_price);
      drawer.querySelector('[data-cart-tax-note]').textContent = next.taxes_included ? 'Taxes included. Delivery calculated at checkout.' : 'Delivery and taxes calculated at checkout.';
      const discounts = drawer.querySelector('[data-cart-discounts]');
      discounts.replaceChildren();
      (next.cart_level_discount_applications || []).forEach(discount => {
        discounts.append(element('li', 'cart-drawer__discount', `${discount.title}: -${money(discount.total_allocated_amount)}`));
      });
      summary.hidden = !next.item_count;
      if (gift) {
        const threshold = Number(gift.dataset.threshold);
        gift.hidden = !(threshold > 0 && gift.dataset.currency === next.currency);
        if (!gift.hidden) {
          const remaining = Math.max(0, threshold - next.total_price);
          const message = remaining > 0 ? `You're ${money(remaining)} away from gift offer eligibility` : 'Threshold reached. Check your gift offer terms.';
          gift.querySelector('[data-cart-gift-message]').textContent = message;
          const progress = gift.querySelector('progress');
          progress.value = Math.min(threshold, Math.max(0, next.total_price));
          progress.setAttribute('aria-valuetext', message);
        }
      }
      if (addon) addon.hidden = !next.item_count || next.items.some(item => String(item.product_id) === addon.dataset.productId);
      cart = next;
      fresh = true;
      uncertainMutation = false;
      refresh.hidden = true;
      document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(next.item_count); });
      document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-label', `Bag, ${next.item_count} items`));
      syncControls();
    }

    async function request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Shopify could not complete that request.');
        return data;
      } finally {
        clearTimeout(timeout);
      }
    }

    function run(task, message, mutation = false, focusTarget = null) {
      pending += 1;
      syncControls();
      queue = queue.then(async () => {
        announce(message);
        try {
          await task();
        } catch (error) {
          fresh = false;
          if (mutation) uncertainMutation = true;
          refresh.hidden = false;
          if (gift) gift.hidden = true;
          const detail = error.name === 'AbortError' ? 'The request timed out.' : error.message || 'The connection failed.';
          announce(`${detail} ${mutation ? 'Your cart may have changed. Refresh it to verify your items before trying again.' : 'Refresh your cart or use View full cart to continue.'}`, true);
        } finally {
          pending -= 1;
          syncControls();
          if (focusTarget && drawer.open && (!document.activeElement || document.activeElement === document.body || document.activeElement === drawer || items.contains(document.activeElement))) {
            const row = Array.from(items.children).find(item => item.dataset.key === focusTarget.key) || items.children[focusTarget.index];
            const control = row?.querySelector(`[data-cart-action="${focusTarget.action}"]`);
            (control && !control.disabled ? control : close).focus({ preventScroll: true });
          }
        }
      });
      return queue;
    }

    async function loadCart(message = 'Your cart is up to date.') {
      render(await request(endpoints.cart));
      announce(message);
    }

    function openDrawer(source) {
      if (drawer.open) return;
      trigger = source || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-expanded', 'true'));
    }

    function finishClose() {
      if (drawer.open) return;
      document.documentElement.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-open]').forEach(node => node.setAttribute('aria-expanded', 'false'));
      const target = trigger?.isConnected && !trigger.disabled ? trigger : document.querySelector('[data-cart-open]');
      target?.focus({ preventScroll: true });
    }
    function closeDrawer() {
      drawer.close();
      finishClose();
    }
    drawer.addEventListener('close', finishClose);
    drawer.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
    close.addEventListener('click', closeDrawer);
    // Own backdrop clicks so theme.js does not restore another dialog's trigger.
    let backdropStart = false;
    const outside = event => {
      const rect = drawer.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    drawer.addEventListener('pointerdown', event => { backdropStart = event.target === drawer && outside(event); });
    drawer.addEventListener('click', event => {
      if (event.target === drawer) {
        event.stopPropagation();
        if (backdropStart && outside(event)) closeDrawer();
        backdropStart = false;
      }
    });
    // Escape uses the native dialog cancel path. The close listener also covers theme.js's cancel handler.
    document.addEventListener('click', event => {
      const opener = event.target.closest('[data-cart-open]');
      if (!opener || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || opener.hasAttribute('download') || (opener.target && opener.target !== '_self')) return;
      openDrawer(opener);
      event.preventDefault();
      run(() => loadCart(), 'Loading your good stuff...');
    });

    document.addEventListener('submit', event => {
      const form = event.target;
      if (event.defaultPrevented || !standardForm(form, event.submitter)) return;
      if (pending) { event.preventDefault(); return; }
      if (uncertainMutation) {
        openDrawer(event.submitter || document.activeElement);
        event.preventDefault();
        announce('Your previous request may have changed the cart. Refresh your cart to check your items before adding again.', true);
        refresh.hidden = false;
        refresh.focus();
        return;
      }
      const data = new FormData(form);
      if (data.getAll('id').length !== 1 || !/^\d+$/.test(String(data.get('id')))) return;
      if (!form.reportValidity()) { event.preventDefault(); return; }
      // Open before cancelling native submission; unsupported enhancement must not swallow an add.
      openDrawer(event.submitter || document.activeElement);
      event.preventDefault();
      run(async () => {
        await request(endpoints.add, { method: 'POST', body: data });
        await loadCart('Added to your bag. Review your good stuff below.');
      }, 'Adding to your bag...', true);
    });

    function changeQuantity(control) {
      if (pending || !fresh || control.disabled) return;
      const row = control.closest('[data-key]');
      const item = cart.items.find(item => item.key === row.dataset.key);
      if (!item) return;
      const input = row.querySelector('input');
      const action = control.dataset.cartAction;
      const step = Number(input.step) || 1;
      const quantity = action === 'remove' ? 0 : action === 'minus' ? item.quantity - step : action === 'plus' ? item.quantity + step : input.valueAsNumber;
      if (action !== 'remove') {
        input.value = String(quantity);
        if (!Number.isSafeInteger(quantity) || !input.checkValidity()) {
          announce(`Enter a whole quantity of at least ${input.min}${input.max ? ` and at most ${input.max}` : ''}, in increments of ${step}. Use Remove to delete this item.`, true);
          input.value = String(item.quantity);
          input.focus();
          return;
        }
      }
      if (quantity === item.quantity) return;
      run(async () => {
        render(await request(endpoints.change, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity }) }));
        const updated = cart.items.find(line => line.key === item.key);
        announce(updated && updated.quantity !== quantity ? 'Shopify adjusted the available quantity. Review your cart.' : quantity === 0 ? 'Item removed from your bag.' : 'Quantity updated.');
      }, 'Updating your bag...', true, { key: item.key, index: Number(row.dataset.index), action });
    }
    items.addEventListener('click', event => {
      const control = event.target.closest('button[data-cart-action]');
      if (control) changeQuantity(control);
    });
    items.addEventListener('change', event => {
      if (event.target.matches('input[data-cart-action]')) changeQuantity(event.target);
    });
    refresh.addEventListener('click', () => { if (!pending) run(() => loadCart(), 'Checking your current cart...'); });
    drawer.querySelector('[data-cart-checkout-form]').addEventListener('submit', event => {
      if (pending || !fresh || !cart?.item_count) event.preventDefault();
    });
    drawer.hidden = false;
    drawer.dataset.ready = 'true';
    document.querySelectorAll('[data-cart-open]').forEach(node => {
      node.setAttribute('aria-haspopup', 'dialog');
      node.setAttribute('aria-controls', drawer.id);
      node.setAttribute('aria-expanded', 'false');
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCart, { once: true });
  else initializeCart();
})();
